/**
 * Locale-aware formatting (QM-D08-003). Gregorian calendar always; Arabic uses Latin digits by default for
 * data tables (configurable). Money is INTEGER minor units + ISO currency.
 */
import type { Locale } from './index';

export interface FormatOptions {
  /** IANA timezone, e.g. Asia/Dubai. Defaults to UTC so server output is deterministic. */
  timeZone?: string;
  /** 'latn' (default) or 'arab' digits for Arabic. */
  numberingSystem?: 'latn' | 'arab';
}

export function localeTag(l: Locale, o: FormatOptions = {}): string {
  const nu = o.numberingSystem ?? 'latn';
  return l === 'ar' ? `ar-AE-u-ca-gregory-nu-${nu}` : 'en-AE-u-ca-gregory-nu-latn';
}

/** Minor units per currency (ISO 4217 exponent). */
const EXPONENT: Record<string, number> = { AED: 2, SAR: 2, USD: 2, EUR: 2, GBP: 2, KWD: 3, BHD: 3, OMR: 3, JPY: 0 };
export const currencyExponent = (c: string) => EXPONENT[c.toUpperCase()] ?? 2;

export function formatCurrency(l: Locale, minor: number, currency = 'AED', o: FormatOptions = {}): string {
  const exp = currencyExponent(currency);
  return new Intl.NumberFormat(localeTag(l, o), { style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: exp, maximumFractionDigits: exp })
    .format(minor / 10 ** exp);
}

export function isValidTimeZone(tz: string): boolean {
  try { new Intl.DateTimeFormat('en', { timeZone: tz }); return true; } catch { return false; }
}

export function formatDateTime(l: Locale, value: Date | string, o: FormatOptions & { style?: 'date' | 'time' | 'datetime' } = {}): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  const style = o.style ?? 'datetime';
  const opts: Intl.DateTimeFormatOptions = { timeZone: o.timeZone ?? 'UTC' };
  if (style !== 'time') Object.assign(opts, { year: 'numeric', month: 'short', day: 'numeric' });
  if (style !== 'date') Object.assign(opts, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return new Intl.DateTimeFormat(localeTag(l, o), opts).format(d);
}

/** Wall-clock parts of an instant in a timezone (used by the digest scheduler and calendar views). */
export function zonedParts(value: Date, timeZone: string): { date: string; hour: number; minute: number } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(value).map((p) => [p.type, p.value]),
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, hour: Number(parts.hour) % 24, minute: Number(parts.minute) };
}
